import React from 'react';
import { Text, View } from 'react-native';
import { ThemeColors } from '@/src/context/ThemeContext';
import { BlogPost } from '@/src/domain/models/BlogPost';

const BracketMakerContent: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      A tournament bracket maker should do more than draw a bracket. The best tools
      handle participant management, automatic bracket generation across multiple formats,
      live result tracking, and sharing — all without requiring organizers to manage a
      spreadsheet. This guide covers what to look for in a free tournament bracket maker
      and walks through how Tournaly handles every step from setup to champion.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      What makes a great tournament bracket maker?
    </Text>
    {[
      { label: 'Multiple bracket formats', desc: 'Single elimination is the minimum. A complete tool also supports double elimination, round robin, and Swiss — the formats that serious organizers actually need.' },
      { label: 'Automatic bracket generation', desc: 'No manual entry of matchup pairs. The tool should seed participants and generate the full bracket structure in one action.' },
      { label: 'Live result tracking', desc: 'Enter scores match by match and let the software propagate winners to the next round automatically.' },
      { label: 'Sharing and viewing', desc: 'Participants and spectators should be able to follow the bracket live without needing an account.' },
      { label: 'Participant management', desc: 'Add players individually, invite by email, or use a shareable code. Players should be easy to add and remove.' },
      { label: 'Export options', desc: 'Print or share the final bracket as an image or PDF — useful for venues, social media, or post-event records.' },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 14 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 4 }}>{item.label}</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary }}>{item.desc}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Supported tournament formats
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Tournaly supports six bracket formats, covering every major tournament structure:
    </Text>
    {[
      'Single Elimination — one loss and you\'re out, the fastest and simplest format (free)',
      'Round Robin — everyone plays everyone, maximum fairness for small groups (free)',
      'Double Elimination — two losses required to eliminate, with Winners and Losers brackets (Pro)',
      'Swiss System — no elimination, score-based pairing, ideal for large fields (Pro)',
      'King of the Hill — winner stays on, sequential gauntlet format (Pro)',
      'Triple Elimination — three lives per player, extended tournament duration (Pro)',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Step-by-step: creating a tournament bracket
    </Text>
    {[
      { step: '1. Create the tournament', desc: 'Name your tournament, choose a format, set the max number of participants, and optionally add rules and a start date. Takes under 60 seconds.' },
      { step: '2. Add participants', desc: 'Add players manually by name, search for registered Tournaly users by email, or share the 8-character invite code so players can join themselves.' },
      { step: '3. Generate the bracket', desc: 'Tap Generate Bracket. Tournaly seeds participants and creates the full match schedule automatically — no configuration required.' },
      { step: '4. Play matches and enter results', desc: 'As matches are played, enter scores in the bracket view. Winners advance automatically, losers drop to the Losers Bracket (in double elimination) or are eliminated.' },
      { step: '5. Share the live bracket', desc: 'Every tournament gets a shareable public link. Participants and spectators can follow the bracket in real time from any device, no account needed.' },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 4 }}>{item.step}</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary }}>{item.desc}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Exporting and sharing your bracket
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Every Tournaly bracket can be exported as a PNG image — perfect for sharing on
      social media, Discord, or posting at a venue. Export to PNG is available on all
      plans. Tournaly Pro adds PDF export, which includes the full bracket layout,
      participant names, scores, and standings in a print-ready format.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Sharing works across devices: the bracket view is fully functional on desktop,
      tablet, and mobile through the web app, or natively through the iOS and Android apps.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Free vs Pro features
    </Text>
    {[
      { label: 'Free plan', desc: '3 active tournaments, up to 16 participants each, Single Elimination and Round Robin formats, 1 active league, PNG export, public sharing' },
      { label: 'Tournaly Pro (€4.99/mo)', desc: 'Unlimited tournaments, up to 512 participants, all 6 bracket formats, unlimited leagues and teams, PDF export, no ads, no watermark, Pro badge' },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 14 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 4 }}>{item.label}</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary }}>{item.desc}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Available on web and mobile
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Tournaly is available as a web app at tournaly.app and as native apps for iOS
      and Android. Organizers typically manage tournaments from the web or mobile app,
      while participants and spectators follow brackets via the public shareable link
      on any device.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.textSecondary }}>
      Create your first tournament bracket for free at tournaly.app — no credit card
      required, no time limit on the free plan.
    </Text>
  </View>
);

const BracketMakerContentEs: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Un creador de brackets de torneo debería hacer más que dibujar un bracket. Las
      mejores herramientas gestionan participantes, generan brackets automáticamente en
      múltiples formatos, rastrean resultados en directo y permiten compartir — todo sin
      que los organizadores tengan que gestionar una hoja de cálculo. Esta guía cubre
      qué buscar en un creador de brackets gratuito y recorre cómo Tournaly gestiona
      cada paso desde la configuración hasta el campeón.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      ¿Qué hace a un gran creador de brackets?
    </Text>
    {[
      { label: 'Múltiples formatos de bracket', desc: 'La eliminación simple es el mínimo. Una herramienta completa también soporta doble eliminación, round robin y suizo — los formatos que los organizadores serios realmente necesitan.' },
      { label: 'Generación automática de brackets', desc: 'Sin entrada manual de emparejamientos. La herramienta debe clasificar a los participantes y generar la estructura completa del bracket en una sola acción.' },
      { label: 'Seguimiento de resultados en directo', desc: 'Introduce puntuaciones partido a partido y deja que el software propague a los ganadores a la siguiente ronda automáticamente.' },
      { label: 'Compartir y ver', desc: 'Los participantes y espectadores deben poder seguir el bracket en directo sin necesidad de una cuenta.' },
      { label: 'Gestión de participantes', desc: 'Añade jugadores individualmente, invita por email o usa un código compartible para que los jugadores se unan ellos mismos.' },
      { label: 'Opciones de exportación', desc: 'Imprime o comparte el bracket final como imagen o PDF — útil para sedes, redes sociales o registros post-evento.' },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 14 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 4 }}>{item.label}</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary }}>{item.desc}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Paso a paso: crear un bracket de torneo
    </Text>
    {[
      { step: '1. Crea el torneo', desc: 'Ponle nombre a tu torneo, elige un formato, establece el número máximo de participantes y opcionalmente añade reglas y una fecha de inicio. Tarda menos de 60 segundos.' },
      { step: '2. Añade participantes', desc: 'Añade jugadores manualmente por nombre, busca usuarios registrados en Tournaly por email, o comparte el código de invitación de 8 caracteres para que los jugadores se unan solos.' },
      { step: '3. Genera el bracket', desc: 'Pulsa Generar Bracket. Tournaly clasifica a los participantes y crea el calendario completo de partidos automáticamente — sin configuración necesaria.' },
      { step: '4. Juega los partidos e introduce resultados', desc: 'Conforme se juegan los partidos, introduce las puntuaciones en la vista de bracket. Los ganadores avanzan automáticamente, los perdedores caen al Bracket de Perdedores (en doble eliminación) o son eliminados.' },
      { step: '5. Comparte el bracket en directo', desc: 'Cada torneo tiene un enlace público compartible. Los participantes y espectadores pueden seguir el bracket en tiempo real desde cualquier dispositivo, sin necesidad de cuenta.' },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 4 }}>{item.step}</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary }}>{item.desc}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Exportar y compartir tu bracket
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Todos los brackets de Tournaly pueden exportarse como imagen PNG — perfecto para
      compartir en redes sociales, Discord o publicar en una sede. La exportación a PNG
      está disponible en todos los planes. Tournaly Pro añade exportación PDF, que incluye
      el diseño completo del bracket, nombres de participantes, puntuaciones y clasificaciones
      en un formato listo para imprimir.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Gratis vs Pro
    </Text>
    {[
      { label: 'Plan gratuito', desc: '3 torneos activos, hasta 16 participantes, Eliminación Simple y Round Robin, 1 liga activa, exportación PNG, compartir público' },
      { label: 'Tournaly Pro (€4,99/mes)', desc: 'Torneos ilimitados, hasta 512 participantes, los 6 formatos de bracket, ligas y equipos ilimitados, exportación PDF, sin anuncios, sin marca de agua, insignia Pro' },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 14 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 4 }}>{item.label}</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary }}>{item.desc}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Disponible en web y móvil
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Tournaly está disponible como app web en tournaly.app y como apps nativas para
      iOS y Android. Los organizadores suelen gestionar torneos desde la app web o móvil,
      mientras que los participantes y espectadores siguen los brackets a través del
      enlace público compartible en cualquier dispositivo.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.textSecondary }}>
      Crea tu primer bracket de torneo gratis en tournaly.app — sin tarjeta de crédito
      y sin límite de tiempo en el plan gratuito.
    </Text>
  </View>
);

export const post: BlogPost = {
  slug: '2026-03-06-tournament-bracket-maker',
  title: 'Free Tournament Bracket Maker: Create and Share Brackets Online',
  titleEs: 'Creador de Brackets de Torneo Gratuito: Crea y Comparte Brackets Online',
  date: '2026-03-06',
  summary: 'How to create a tournament bracket online for free — step-by-step guide covering participant setup, automatic bracket generation, live result tracking, and sharing with Tournaly.',
  summaryEs: 'Cómo crear un bracket de torneo online gratis — guía paso a paso que cubre la configuración de participantes, generación automática de brackets, seguimiento de resultados en directo y cómo compartirlo con Tournaly.',
  tags: ['Guide', 'Brackets', 'How-to'],
  Content: BracketMakerContent,
  ContentEs: BracketMakerContentEs,
};
